import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { RegistryDocument } from '../../data/types';
import DocumentViewerDialog from '../../documents/DocumentViewerDialog';
import { buildAmmaStatement } from '../../domain/taxOutputs';
import { date, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useInvestorScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { AboutThisPage } from '../../ui/primitives';
import { Card, InvestorTitle, SectionLabel, shortEntityName } from '../common';

const YEARS = ['FY2027', 'FY2026'];

export default function TaxCentrePage() {
  const scope = useInvestorScope();
  const { state } = useRegistry();
  const toast = useToast();
  const [year, setYear] = useState('FY2027');
  const [viewing, setViewing] = useState<RegistryDocument | null>(null);

  const summaries = useMemo(
    () =>
      scope.holders.map((holder) => {
        const statement = buildAmmaStatement(state, holder.id, year);
        const t = statement?.totals;
        const totals = {
          gross: t?.gross ?? 0,
          interest: t?.interest ?? 0,
          otherIncome: t?.otherIncome ?? 0,
          discountedGain: t?.discountedGainCash ?? 0,
          taxDeferred: t?.taxDeferred ?? 0,
          withheld: (t?.tfnWithheld ?? 0) + (t?.nrWithheld ?? 0),
        };
        const statements = scope.documents.filter(
          (doc) =>
            doc.holderId === holder.id && doc.kind === 'TAX_STATEMENT' && doc.name.includes(year),
        );
        return { holder, totals, statements };
      }),
    [scope, state, year],
  );

  return (
    <>
      <InvestorTitle
        title="Tax centre"
        subtitle="Annual tax statements (AMMA) and income for each entity, ready for your accountant."
        action={
          <Button
            variant="outlined"
            onClick={() => toast('All statements for the year downloaded as a ZIP', 'info')}
          >
            Download all
          </Button>
        }
      />
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {YEARS.map((item) => (
          <Chip
            key={item}
            label={item}
            color={year === item ? 'primary' : 'default'}
            variant={year === item ? 'filled' : 'outlined'}
            onClick={() => setYear(item)}
          />
        ))}
      </Stack>
      <AboutThisPage>
        <p>
          Statements are issued after 30 June once the fund accounts are final. During the year you
          can see income received so far, split by estimated tax component. You can give your
          accountant read-only access from your profile.
        </p>
      </AboutThisPage>
      <Stack spacing={2}>
        {summaries.map(({ holder, totals, statements }) => (
          <Card key={holder.id}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                  {shortEntityName(holder)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {holder.tfnStatus === 'NOT_QUOTED'
                    ? 'TFN not provided — tax is being withheld'
                    : 'TFN/ABN on file'}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 800 }}>
                {money(totals.gross, true)} gross income
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
                mt: 2,
              }}
            >
              {[
                ['Interest', totals.interest],
                ['Other income', totals.otherIncome],
                ['Capital gains', totals.discountedGain],
                ['Tax-deferred', totals.taxDeferred],
                ['Tax withheld (credit)', totals.withheld],
              ].map(([label, value]) => (
                <Box
                  key={String(label)}
                  sx={{ bgcolor: 'background.default', borderRadius: '6px', p: 1.25 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {money(Number(value), true)}
                  </Typography>
                </Box>
              ))}
            </Box>
            <SectionLabel>Statements</SectionLabel>
            {statements.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {year === 'FY2027'
                  ? 'Your FY2027 statement will be available in September 2027.'
                  : 'No statement for this year.'}
              </Typography>
            ) : (
              statements.map((doc) => (
                <Stack
                  key={doc.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 0.75 }}
                >
                  <Typography variant="body2">{doc.name}</Typography>
                  <Button size="small" onClick={() => setViewing(doc)}>
                    Download · {date(doc.date)}
                  </Button>
                </Stack>
              ))
            )}
          </Card>
        ))}
      </Stack>
      <DocumentViewerDialog doc={viewing} onClose={() => setViewing(null)} />
    </>
  );
}
